import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, Star } from "lucide-react";
import api from "@/lib/api";
import SEOHead from "@/components/site/SEOHead";
import Reveal from "@/components/site/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useEnquiry } from "@/components/site/PublicLayout";
import { trackEvent, useSettings } from "@/lib/settings";
import SmartMedia from "@/components/site/SmartMedia";
import { mediaImage } from "@/lib/media";
import { ensureLayout, coreMeta } from "@/lib/layout";
import { CustomSection, SectionCTA } from "@/components/site/PropertySections";
import Lightbox from "@/components/site/Lightbox";
import DraftBanner from "@/components/site/DraftBanner";
import { breadcrumbs, faqPage } from "@/lib/schema";

const ov = (sec, key) => sec.data?.overline || coreMeta(key)?.defaults?.overline || "";
const hd = (sec, key) => sec.data?.heading || coreMeta(key)?.defaults?.heading || "";

function CtaBtn({ cfg, enquiry, slug, sitePhone, className, testId, children }) {
  const action = cfg.action || (cfg.url ? "link" : "enquire");
  if (action === "call") {
    const ph = String(cfg.phone || sitePhone || "").replace(/[^\d+]/g, "");
    if (ph) return <a href={`tel:${ph}`} className={className} data-testid={testId}>{children}</a>;
  }
  if (action === "link" && cfg.url) {
    return <a href={cfg.url} className={className} data-testid={testId}>{children}</a>;
  }
  return <button type="button" onClick={() => enquiry.open(slug)} className={className} data-testid={testId}>{children}</button>;
}

function PropertyGallery({ p, sec }) {
  const [lbIndex, setLbIndex] = useState(null);
  if (!p.gallery?.length) return null;
  return (
    <section id="gallery" className="container-x py-8 md:py-12" data-testid="property-gallery">
      <Reveal><div className="overline accent-color">{ov(sec, "gallery")}</div></Reveal>
      <Reveal delay={100}><h2 className="display-2 mt-6">{hd(sec, "gallery")}</h2></Reveal>
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
        {p.gallery.map((url, i) => (
          <Reveal key={i} delay={i * 50} className={i % 5 === 0 ? "md:col-span-2 md:row-span-2 aspect-square" : "aspect-[3/4]"}>
            <button type="button" onClick={() => setLbIndex(i)} className="w-full h-full zoom-on-hover relative cursor-zoom-in block text-left" aria-label={`View photo ${i + 1}`} data-testid={`property-gallery-item-${i}`}>
              <SmartMedia media={url} alt={`${p.name} ${i}`} className="w-full h-full object-cover" />
            </button>
          </Reveal>
        ))}
      </div>
      <Lightbox items={p.gallery} index={lbIndex} setIndex={setLbIndex} altPrefix={p.name} />
    </section>
  );
}

const CORE_RENDERERS = {
  intro: (p, sec) => (
    <section className="container-x py-8 md:py-12" data-testid="property-intro">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="overline accent-color">{ov(sec, "intro")}</div>
          <h2 className="display-2 mt-6">{p.intro?.title}</h2>
        </Reveal>
        <Reveal delay={150} className="lg:col-span-6 lg:col-start-7">
          <p className="body-lead">{p.intro?.body}</p>
          <div className="hairline mt-10" />
          <div className="grid grid-cols-2 gap-6 mt-8 text-sm">
            <div><div className="overline accent-color">Location</div><div className="mt-2 font-medium">{p.location}</div></div>
            {p.amenities?.length > 0 && (
              <div><div className="overline accent-color">Amenities</div><div className="mt-2 text-graphite">{p.amenities.slice(0, 4).join(" · ")}</div></div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  ),

  usps: (p, sec) => p.usps?.length > 0 && (
    <section className="bg-ivory py-8 md:py-12" data-testid="property-usps">
      <div className="container-x">
        <Reveal><div className="overline accent-color">{ov(sec, "usps")}</div></Reveal>
        <Reveal delay={100}><h2 className="display-2 mt-6 max-w-3xl">{hd(sec, "usps")}</h2></Reveal>
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10">
          {p.usps.map((u, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="p-8 bg-ivory h-full">
                <div className="number-marker accent-color">0{i + 1}</div>
                <div className="font-display text-2xl mt-4">{u.title}</div>
                <div className="mt-3 text-sm text-graphite leading-relaxed font-light">{u.description}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  ),

  rooms: (p, sec) => p.rooms?.length > 0 && (
    <section className="container-x py-8 md:py-12" data-testid="property-rooms">
      <div className="flex items-end justify-between mb-12">
        <div>
          <Reveal><div className="overline accent-color">{ov(sec, "rooms")}</div></Reveal>
          <Reveal delay={100}><h2 className="display-2 mt-6">{hd(sec, "rooms")}</h2></Reveal>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
        {p.rooms.map((r, i) => (
          <Reveal key={i} delay={i * 80}>
            <div className="group">
              <div className="aspect-[4/5] zoom-on-hover overflow-hidden relative">
                <SmartMedia media={r.image_url} alt={r.name} className="w-full h-full object-cover" />
              </div>
              <div className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <div className="font-display text-2xl">{r.name}</div>
                  <div className="mt-2 text-sm text-graphite font-light leading-relaxed">{r.description}</div>
                </div>
                <div className="text-xs accent-color whitespace-nowrap pt-1">{r.price_text}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  ),

  spaces: (p, sec) => p.event_spaces?.length > 0 && (
    <section className="bg-ivory py-8 md:py-12" data-testid="property-event-spaces">
      <div className="container-x">
        <Reveal><div className="overline accent-color">{ov(sec, "spaces")}</div></Reveal>
        <Reveal delay={100}><h2 className="display-2 mt-6 max-w-2xl">{hd(sec, "spaces")}</h2></Reveal>
        <div className="mt-16 space-y-20">
          {p.event_spaces.map((s, i) => (
            <Reveal key={i} delay={i * 60}>
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
                <div className="lg:col-span-7 [direction:ltr]">
                  <div className="aspect-[16/10] overflow-hidden zoom-on-hover relative">
                    <SmartMedia media={s.image_url} alt={s.name} className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="lg:col-span-5 [direction:ltr]">
                  <div className="number-marker accent-color">Space · 0{i + 1}</div>
                  <div className="font-display text-3xl md:text-4xl mt-3">{s.name}</div>
                  <div className="overline accent-color mt-3">{s.capacity}</div>
                  <div className="mt-4 body-lead">{s.description}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  ),

  occasion: (p, sec, enquiry) => (
    <section className="container-x py-8 md:py-12" data-testid="property-occasion">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
        {[p.wedding_section, p.corporate_section].filter(Boolean).map((s, i) => (
          <Reveal key={i} delay={i * 120}>
            <div className="group">
              <div className="aspect-[4/5] overflow-hidden zoom-on-hover relative">
                <SmartMedia media={s.image_url} alt={s.title} className="w-full h-full object-cover" />
              </div>
              <div className="overline accent-color mt-6">{i === 0 ? "Weddings" : "Corporate"}</div>
              <div className="font-display text-3xl md:text-4xl mt-3">{s.title}</div>
              <div className="mt-4 body-lead">{s.description}</div>
              <button onClick={() => enquiry.open(p.slug)} className="link-arrow mt-6">Enquire about {i === 0 ? "weddings" : "retreats"} <ArrowRight className="w-4 h-4" /></button>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  ),

  packages: (p, sec, enquiry) => p.pricing_packages?.length > 0 && (
    <section className="bg-ink text-ivory py-8 md:py-12" data-testid="property-packages">
      <div className="container-x">
        <Reveal><div className="overline text-ivory/60">{ov(sec, "packages")}</div></Reveal>
        <Reveal delay={100}><h2 className="display-2 mt-6 text-ivory max-w-3xl">{hd(sec, "packages")}</h2></Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-ivory/10">
          {p.pricing_packages.map((pk, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="p-10 bg-ink h-full flex flex-col">
                <div className="overline accent-color">{pk.name}</div>
                <div className="font-display text-5xl md:text-6xl mt-6 text-ivory">{pk.price}</div>
                <div className="hairline bg-ivory/20 my-8" />
                <ul className="space-y-3 text-sm text-ivory/80">
                  {pk.features?.map((f, j) => (
                    <li key={j} className="flex items-start gap-3"><span className="accent-color">—</span>{f}</li>
                  ))}
                </ul>
                <button onClick={() => enquiry.open(p.slug)} className="mt-10 self-start border border-ivory/40 text-ivory px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-ivory hover:text-ink transition-colors">
                  Get a quote
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  ),

  gallery: (p, sec) => <PropertyGallery p={p} sec={sec} />,

  video: (p, sec) => p.video_url && (
    <section className="surface-bg py-8 md:py-12" data-testid="property-video">
      <div className="container-x">
        <Reveal><div className="overline accent-color">{ov(sec, "video")}</div></Reveal>
        <Reveal delay={100}><h2 className="display-2 mt-6 max-w-2xl">{hd(sec, "video")}</h2></Reveal>
        <Reveal delay={200} className="mt-12">
          <div className="aspect-video w-full">
            <iframe src={p.video_url} title="Property video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen onLoad={() => trackEvent("video_play", { property: p.slug })} />
          </div>
        </Reveal>
      </div>
    </section>
  ),

  reviews: (p, sec) => p.reviews?.length > 0 && (
    <section className="container-x py-8 md:py-12" data-testid="property-reviews">
      <Reveal><div className="overline accent-color">{ov(sec, "reviews")}</div></Reveal>
      <Reveal delay={100}><h2 className="display-2 mt-6">{hd(sec, "reviews")}</h2></Reveal>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
        {p.reviews.map((r, i) => (
          <Reveal key={i} delay={i * 80}>
            <div className="bg-ivory p-10 h-full">
              <div className="flex gap-1">
                {Array.from({ length: r.rating || 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-current accent-color" />)}
              </div>
              <p className="mt-6 font-display text-2xl leading-snug">&ldquo;{r.text}&rdquo;</p>
              <div className="hairline mt-8" />
              <div className="mt-4 text-sm">{r.name} <span className="text-graphite ml-2">· {r.source}</span></div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  ),

  faqs: (p, sec) => p.faqs?.length > 0 && (
    <section className="bg-ivory py-8 md:py-12" data-testid="property-faqs">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal><div className="overline accent-color">{ov(sec, "faqs")}</div></Reveal>
          <Reveal delay={100}><h2 className="display-2 mt-6">{hd(sec, "faqs")}</h2></Reveal>
        </div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="border-t border-ink/10">
            {p.faqs.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-b border-ink/10">
                <AccordionTrigger className="text-left py-6 font-display text-xl md:text-2xl hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="pb-6 text-sm text-graphite font-light leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  ),

  location: (p, sec, enquiry, sitePhone) => (
    <section className="container-x py-8 md:py-12" data-testid="property-location">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <Reveal><div className="overline accent-color">{ov(sec, "location")}</div></Reveal>
          <Reveal delay={100}><h2 className="display-2 mt-6">{sec.data?.heading || p.location}</h2></Reveal>
          <Reveal delay={200}>
            <div className="mt-8 space-y-4 text-sm">
              <div className="flex items-center gap-3"><MapPin className="w-4 h-4 accent-color" /> {p.location}</div>
              <CtaBtn cfg={sec.data?.cta?.enabled ? { text: sec.data.cta.label || "Plan a visit", action: sec.data.cta.action, url: sec.data.cta.url, phone: sec.data.cta.phone } : { text: "Plan a visit" }} enquiry={enquiry} slug={p.slug} sitePhone={sitePhone} className="btn-primary mt-4 inline-block" testId="property-bottom-cta">{sec.data?.cta?.enabled ? (sec.data.cta.label || "Plan a visit") : "Plan a visit"}</CtaBtn>
            </div>
          </Reveal>
        </div>
        <Reveal delay={150} className="lg:col-span-7 aspect-[16/12]">
          <iframe
            title="Map"
            className="w-full h-full"
            src={`https://www.google.com/maps?q=${encodeURIComponent(p.location || "Bangalore")}&output=embed`}
            loading="lazy"
          />
        </Reveal>
      </div>
    </section>
  ),

  links: (p, sec) => (
    <section className="bg-ink text-ivory py-20" data-testid="property-internal-links">
      <div className="container-x">
        <div className="overline text-ivory/60">{ov(sec, "links")}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 mt-8">
          {["kai", "white-mist", "yellow-moon", "aviana"].filter((s) => s !== p.slug).map((s, i) => (
            <Link key={s} to={`/${s}`} className="group" data-testid={`internal-link-${s}`}>
              <div className="overline text-ivory/50">0{i + 1}</div>
              <div className="font-display text-3xl md:text-4xl mt-2 group-hover:text-clay transition-colors">{s.replace("-", " ")}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  ),
};

export default function Property() {
  const { slug } = useParams();
  const enquiry = useEnquiry();
  const { settings } = useSettings();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setP(null); setNotFound(false);
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get(`/public/properties/${slug}`)
      .then((r) => { setP(r.data); trackEvent("property_page_view", { property: slug }); })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-x pt-40 pb-32 text-center">
        <div className="overline">404</div>
        <h1 className="display-2 mt-4">Property not found</h1>
        <Link to="/" className="link-arrow mt-8 inline-flex">Back to home <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }
  if (!p) return <div className="pt-32 pb-32 container-x"><div className="animate-pulse h-96 bg-bone" /></div>;

  const theme = p.theme || "kai";
  const seo = p.seo || {};
  const layout = ensureLayout(p.layout).filter((s) => s.enabled !== false);
  const sitePhone = settings?.contact?.phone || "";
  const heroCta = { text: p.hero?.cta_text || "Enquire now", action: p.hero?.cta_action, url: p.hero?.cta_url, phone: p.hero?.cta_phone };
  const heroCta2 = { text: p.hero?.cta2?.text ?? "View gallery", action: p.hero?.cta2?.action ?? "link", url: p.hero?.cta2?.url ?? "#gallery", phone: p.hero?.cta2?.phone };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Resort",
    name: p.name,
    description: seo.meta_description || p.tagline,
    image: mediaImage(p.hero?.image_url),
    address: { "@type": "PostalAddress", addressLocality: p.location },
    geo: p.lat ? { "@type": "GeoCoordinates", latitude: p.lat, longitude: p.lng } : undefined,
  };

  // Auto schema.org ItemList for "Celebrations hosted" sections
  const celebrationLists = layout
    .filter((s) => s.type === "celebrations" && (s.data?.items || []).some((it) => it && it.title))
    .map((s) => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: s.data?.heading || `Celebrations hosted at ${p.name}`,
      itemListElement: (s.data.items || []).filter((it) => it && it.title).map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.title,
        description: it.description || undefined,
      })),
    }));
  const schemas = [
    schema,
    breadcrumbs([{ name: "Home", path: "/" }, { name: p.name, path: `/${p.slug}` }]),
    ...(p.faqs?.length ? [faqPage(p.faqs)] : []),
    ...celebrationLists,
  ];

  return (
    <div data-theme={theme} className="surface-bg">
      <SEOHead title={seo.meta_title} description={seo.meta_description} keywords={seo.keywords} ogImage={seo.og_image || mediaImage(p.hero?.image_url)} schema={schemas} />
      <DraftBanner show={p.is_published === false} />

      {/* HERO (always first) */}
      <section className="relative min-h-[100svh] flex items-end" data-testid="property-hero">
        <SmartMedia media={p.hero?.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover" eager />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/20 via-transparent to-ink/80" />
        <div className="relative container-x pb-16 md:pb-24 pt-44 md:pt-52 text-ivory w-full">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-ivory/80 animate-fade-up">
            <span>Happy Retreats</span><span>·</span><span>{p.location}</span>
          </div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-[140px] leading-none tracking-tight mt-8 animate-fade-up" style={{ animationDelay: "0.2s" }}>
            {p.name}
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-xl font-light italic animate-fade-up" style={{ animationDelay: "0.4s" }}>
            {p.tagline}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "0.6s" }}>
            <CtaBtn cfg={heroCta} enquiry={enquiry} slug={p.slug} sitePhone={sitePhone} testId="property-hero-cta" className="bg-ivory text-ink px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-[var(--prop-accent)] hover:text-white transition-colors">{heroCta.text}</CtaBtn>
            {heroCta2.text ? <CtaBtn cfg={heroCta2} enquiry={enquiry} slug={p.slug} sitePhone={sitePhone} testId="property-hero-cta2" className="border border-ivory/40 text-ivory px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-ivory hover:text-ink transition-colors">{heroCta2.text}</CtaBtn> : null}
          </div>
        </div>
      </section>

      {layout.map((sec) => {
        if (sec.type.startsWith("core:")) {
          const key = sec.type.slice(5);
          const R = CORE_RENDERERS[key];
          if (!R) return null;
          const node = R(p, sec, enquiry, sitePhone);
          return node ? <div key={sec.id}>{node}{key !== "location" && <SectionCTA cta={sec.data?.cta} enquiry={enquiry} slug={p.slug} />}</div> : null;
        }
        return <CustomSection key={sec.id} sec={sec} enquiry={enquiry} slug={p.slug} />;
      })}
    </div>
  );
}
